import { faCommentSms } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Client from '@spike/client-model';
import { ClientsStatus, searchClientsThunk } from '@spike/clients-action';
import { ApiClientWrapper } from '@spike/api-client';
import SearchBar from 'components/UI/V3/SearchBar/SearchBar';
import Spinner from 'components/UI/V3/Spinner';
import { useDebounce } from 'hooks/useDebounce';
import { FunctionComponent, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';
import { RootState } from 'store';
import { realEmail } from '../contactEmail';
import InboxAvatar from '../InboxAvatar/InboxAvatar';
import { canTextPhone, looksLikePhone, Recipient, recipientFromClient } from './recipient';
import styles from './RecipientPicker.module.scss';

interface RecipientPickerProps {
    /** Scoped to the selected location. */
    apiClientWrapper: ApiClientWrapper;
    onPick: (recipient: Recipient) => void;
}

const MIN_SEARCH_LENGTH = 2;
const SEARCH_PAGE_SIZE = 20;

const clientName = (client: Client): string =>
    `${client.firstName} ${client.lastName}`.trim() || realEmail(client.email) || client.phone;

/**
 * "To" field of a new conversation: search clients (org-wide, same thunk as legacy /sms) or type a
 * phone number; a phone-looking text offers "Text <number>" and the backend resolves it to a client
 * or creates a prospect (CUD-5583).
 */
const RecipientPicker: FunctionComponent<RecipientPickerProps> = ({ apiClientWrapper, onPick }) => {
    const dispatch = useDispatch<ThunkDispatch<RootState, null, AnyAction>>();
    const clients = useSelector<RootState, Array<Client>>(state => state.clients.list);
    const clientsStatus = useSelector<RootState, ClientsStatus>(state => state.clients.status);

    const [query, setQuery] = useState('');
    const debouncedQuery = useDebounce(query.trim(), 300);

    const isPhone = looksLikePhone(query);
    const canText = canTextPhone(query);
    const canSearchClients = debouncedQuery.length >= MIN_SEARCH_LENGTH && !looksLikePhone(debouncedQuery);
    const searching = canSearchClients && clientsStatus === ClientsStatus.Fetching;
    // client results belong to the debounced query; hide them the moment the live text turns into a phone
    const showResults = canSearchClients && !searching && !isPhone;

    useEffect(() => {
        if (!canSearchClients) return;
        dispatch(searchClientsThunk(apiClientWrapper, debouncedQuery, 1, SEARCH_PAGE_SIZE));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debouncedQuery, canSearchClients]);

    return (
        <div className={styles.root}>
            <SearchBar
                placeholder="Search a client by name, email or phone"
                searchText={query}
                onChangeSearchText={setQuery}
            />

            {(isPhone || searching || showResults) && (
                <div className={styles.results}>
                    {isPhone && (
                        <button
                            type="button"
                            className={styles.result}
                            disabled={!canText}
                            onClick={() => onPick({ kind: 'phone', phone: query.trim() })}
                        >
                            <span className={styles.phoneIcon}>
                                <FontAwesomeIcon icon={faCommentSms} />
                            </span>
                            <span className={styles.resultBody}>
                                <span className={styles.resultName}>Text {query.trim()}</span>
                                <span className={styles.resultMeta}>
                                    New number — we will match it to a client or create a prospect
                                </span>
                            </span>
                        </button>
                    )}

                    {searching && (
                        <div className={styles.searching}>
                            <Spinner />
                        </div>
                    )}

                    {showResults &&
                        clients.map(client => (
                            <button
                                key={client.id}
                                type="button"
                                className={styles.result}
                                onClick={() => onPick(recipientFromClient(client))}
                            >
                                <InboxAvatar name={clientName(client)} />
                                <span className={styles.resultBody}>
                                    <span className={styles.resultName}>{clientName(client)}</span>
                                    <span className={styles.resultMeta}>
                                        {[client.phone, realEmail(client.email)].filter(Boolean).join(' · ')}
                                    </span>
                                </span>
                            </button>
                        ))}

                    {showResults && clients.length === 0 && (
                        <p className={styles.empty}>No clients match “{debouncedQuery}”.</p>
                    )}
                </div>
            )}
        </div>
    );
};

export default RecipientPicker;
